import { Stack } from 'expo-router';
import React from 'react';
import { Platform, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Hourglass } from '@/ui/icons/hourglass';

interface PaymentVerificationPendingProps {
  onDone: () => void;
}

export function PaymentVerificationPending({
  onDone,
}: PaymentVerificationPendingProps) {
  const { styles, theme } = useStyles(stylesheet);
  const isDesktop = ['md', 'lg'].includes(UnistylesRuntime.breakpoint);

  function renderBackButton() {
    return (
      <NavigationButton
        type={NavigationButtonTypes.back}
        onPress={onDone}
        style={styles.backButton}
      />
    );
  }

  const screenOptions = isDesktop
    ? { headerShown: false }
    : {
        title: 'Verification In Progress',
        headerTitle: 'Verification In Progress',
        headerLeft: renderBackButton,
        headerShown: true,
        headerStyle: {
          backgroundColor: theme.colors.background_1,
        },
        headerTintColor: theme.colors.text.primary,
        headerShadowVisible: false,
      };

  return (
    <View style={styles.page}>
      <View style={styles.container}>
        <Stack.Screen options={screenOptions} />

        {isDesktop ? (
          <View style={styles.desktopHeader}>
            <View style={styles.desktopBack}>
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={onDone}
              />
            </View>

            <Text type="largeTitle" style={styles.centered}>
              Verify Your Payment Method
            </Text>
          </View>
        ) : null}

        <View style={styles.content}>
          <View style={styles.message}>
            <View style={styles.circle}>
              <Icon
                type="naked"
                icon={<Hourglass />}
                fillColor={theme.colors.chip.pending.color}
                width={12}
                height={12}
              />
            </View>

            <Text type="title2" style={styles.centered}>
              Verification In Progress
            </Text>

            <Text
              type="bodyRegular"
              style={[styles.centered, { color: theme.colors.text.secondary }]}
            >
              We’re reviewing your screenshot and will notify you when it’s
              done.
            </Text>
          </View>

          <Button text="Done" type="primary" onPress={onDone} />
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  page: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    gap: {
      md: 16,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
  backButton: {
    marginLeft: Platform.OS === 'web' ? 16 : 0,
  },
  desktopHeader: {
    gap: 16,
    paddingHorizontal: 16,
  },
  desktopBack: {
    alignSelf: 'flex-start',
  },
  content: {
    flex: 1,
    justifyContent: 'space-between',
    padding: 16,
    paddingBottom: 32,
    gap: 16,
  },
  message: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  circle: {
    width: 36,
    height: 36,
    borderRadius: 100,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.chip.pending.backgroundColor,
  },
  centered: {
    textAlign: 'center',
  },
}));
